import { useCallback, useState } from 'react';
import { ApiError } from 'src/api/axiosClient/types';
import { buildFormData, buildRequestData } from '../helpers';
import { useBlocklistService } from '../services';
import { BlocklistItem, BlocklistEntryFormData } from '../types';

export interface BlocklistEntryFormDialogController {
  isOpen: boolean;
  initFormData: BlocklistEntryFormData | null;
  error: ApiError | null;
  open: (item?: BlocklistItem, initialWord?: string) => void;
  close: () => void;
  submit: (
    full: BlocklistEntryFormData,
    dirty: Partial<BlocklistEntryFormData>,
    nextAction?: () => unknown | Promise<unknown>,
  ) => Promise<void>;
}

export const useBlocklistEntryFormDialogController = (): BlocklistEntryFormDialogController => {
  const [initFormData, setInitFormData] = useState<BlocklistEntryFormData | null>(null);

  const { createBlocklistItem, updateBlocklistItem, error } = useBlocklistService();

  const open = useCallback((item?: BlocklistItem, initialWord?: string) => {
    setInitFormData(buildFormData(item, initialWord));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (
      full: BlocklistEntryFormData,
      dirty: Partial<BlocklistEntryFormData>,
      nextAction?: () => Promise<unknown>,
    ) => {
      const { id: itemId } = full;

      try {
        if (itemId) {
          await updateBlocklistItem(itemId, buildRequestData(full));
        } else {
          await createBlocklistItem(buildRequestData(full));
        }

        close();
        await nextAction?.();
      } catch (e) {
        console.log(e);
      }
    },
    [createBlocklistItem, updateBlocklistItem, close],
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    submit,
  };
};
